import { DirectionProvider } from "@radix-ui/react-direction";
import { getDirection } from "@shared/utils/rtl";
import { useEffect } from "react";
import { Button } from "@shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@shared/components/ui/dropdown-menu";
import { Check, Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

const languages = [
  { code: "en", name: "English" },
  { code: "ar", name: "العربية" },
  { code: "fr", name: "Français" },
];

export function LanguageSwitcher() {
  const { t, i18n: hookI18n } = useTranslation();

  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage && hookI18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [hookI18n]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full bg-muted text-foreground p-2"
        >
          <Languages size={16} />
          <span className="sr-only">{t("common.changeLanguage")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="bottom">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
          >
            <div className="flex items-center justify-between w-full">
              <span>{lang.name}</span>
              {hookI18n.language === lang.code && (
                <Check className="h-4 w-4 ml-2" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const LanguageWrapper = ({ children }) => {
  const { i18n: hookI18n } = useTranslation();
  const direction = getDirection(hookI18n.language);

  useEffect(() => {
    document.body.dir = direction;
    document.documentElement.lang = hookI18n.language;
  }, [hookI18n, hookI18n.language, direction]);

  return (
    <>
      <DirectionProvider dir={direction}>{children}</DirectionProvider>
    </>
  );
};
